import React, { useState, useContext, useEffect, forwardRef, useImperativeHandle } from 'react';
import VisitorParamsContext from '../../../Contexts/VisitorParams';
import Telephone from '../../UI/Telephone';
import { numberValidation } from '../../../Utility/Validations';
import { usePhoneValidation } from '../../../Hooks/usePhoneValidation';
import { useEmailValidation } from '../../../Hooks/useEmailValidation';
import Email from '../../UI/Email';

const InfoBanner = () => (
  <div className="d-flex align-items-center gap-4 bg-blue-50-half rounded-4 p-3 mt-5 border border-blue-100 shadow-sm animate-pop-in">
    <div className="flex-shrink-0 bg-white rounded-3 shadow-sm d-flex align-items-center justify-content-center p-2 w-12 h-12">
      <svg viewBox="0 0 100 100" className="w-100 h-100">
        <circle cx="50" cy="35" r="15" fill="#cfd8dc" />
        <rect x="30" y="55" width="40" height="30" rx="10" fill="#2196f3" />
        <circle cx="20" cy="50" r="10" fill="#4caf50" />
        <path d="M15 50 L18 53 L25 45" stroke="white" strokeWidth="3" fill="none" />
      </svg>
    </div>
    <p className="fw-semibold text-gray-600 leading-tight mb-0 fs-12">
      Your personal information is vital for verifying your identity and finance agreement,
      allowing us to assess your PCP claim accurately.
    </p>
  </div>
);

const ContactInfo = forwardRef(({
  slideView,
  previousSlideChange,
  register,
  validationMsg,
  trigger,
  clearErrors,
  formSubmit,
  isLoading,
  isButtonDisabled,
  getValues,
  setError,
  watch,
  isValid,
  setIsNextActive,
}, ref) => {
  const { visitorParameters } = useContext(VisitorParamsContext);
  const { emailValidation } = useEmailValidation();
  const { phoneValidation } = usePhoneValidation();

  const [contactValidation, setContactValidation] = useState({
    txtPhone: false,
    txtEmail: false,
  });

  const email = watch('txtEmail')?.trim();
  const phone = watch('txtPhone')?.trim();

  // Button is active when both fields have values and aren't mid-submission
  const isButtonActive = Boolean(email && phone && !isButtonDisabled && !isLoading);

  // Keep parent sticky button in sync
  useEffect(() => {
    if (slideView !== 'show') return;
    setIsNextActive(isButtonActive);
  }, [email, phone, isButtonDisabled, isLoading, slideView]);

  // Auto-focus the first field when this slide appears
  useEffect(() => {
    if (slideView === 'show') {
      const timer = setTimeout(() => {
        document.getElementById('txtEmail')?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [slideView]);
  const phoneValidCheck = async () => {
    const values = getValues();
    const phoneVal = values['txtPhone'].replace(/^0+/, '');
    if (!phoneVal) {
      setError('txtPhone', { type: 'manual', message: 'Please Enter Valid Phone Number' });
      return 0;
    }
    const res = await phoneValidation(phoneVal, visitorParameters.visitor_parameters.uuid);
    if (res.data.status_code === 0) {
      setContactValidation((prev) => ({ ...prev, txtPhone: false }));
      setError('txtPhone', { type: 'manual', message: 'Please Enter Valid Working Phone Number' });
      return 0;
    } else if (res.data.status_code === 2) {
      setContactValidation((prev) => ({ ...prev, txtPhone: false }));
      setError('txtPhone', { type: 'manual', message: 'Phone Number Already Exist' });
      return 0;
    } else if (res.data.status_code === 3) {
      setContactValidation((prev) => ({ ...prev, txtPhone: false }));
      setError('txtPhone', { type: 'manual', message: 'Please Enter Valid Phone Number' });
      return 0;
    } else {
      setContactValidation((prev) => ({ ...prev, txtPhone: true }));
      clearErrors('txtPhone');
      return 1;
    }
  };

  const emailValidCheck = async () => {
    const values = getValues();
    const emailVal = values['txtEmail'];
    if (!emailVal) {
      setError('txtEmail', { type: 'manual', message: 'Please Enter Valid Email' });
      return 0;
    }
    const res = await emailValidation(emailVal, visitorParameters.visitor_parameters.uuid);
    if (res.data.status_code === 1) {
      setContactValidation((prev) => ({ ...prev, txtEmail: true }));
      clearErrors('txtEmail');
      return 1;
    } else {
      setContactValidation((prev) => ({ ...prev, txtEmail: false }));
      setError('txtEmail', { type: 'manual', message: 'Invalid Email' });
      return 0;
    }
  };

  const eventOnchange = (e) => {
    const field = e.target.id;
    setContactValidation((prev) => ({ ...prev, [field]: false }));
    if (e.target.name === 'txtPhone' && e.target.value.length >= 11) {
      phoneValidCheck();
    }
  };

  const handleNext = async (e) => {
    e.preventDefault();
    const isFrontendValid = await trigger(['txtEmail', 'txtPhone']);
    if (!isFrontendValid) return;
    const isEmailOk = contactValidation.txtEmail || (await emailValidCheck() === 1);
    const isPhoneOk = contactValidation.txtPhone || (await phoneValidCheck() === 1);
    if (isEmailOk && isPhoneOk) formSubmit();
  };

  // Expose handleNext to parent via ref
  useImperativeHandle(ref, () => ({ handleNext }));

  return (
    <div className={`slide ${slideView === 'show' ? 'active' : 'd-none'}`} id="slide-4">
      <h2 className="fs-4 fw-bold tracking-tight text-gray-900 animate-stagger-up delay-200">
        Contact Info
      </h2>
      <p className="fw-medium text-gray-700 mb-5 animate-stagger-up delay-200 fs-15">
        How can we reach you if you're eligible?
      </p>

      <div className="mx-auto animate-stagger-up delay-400">
        {/* Email */}
        <fieldset className="input-wrapper border rounded p-0 pb-1 px-3 position-relative w-100 mb-4 transition-colors rounded-10">
          <legend className="px-1 ms-2 fw-semibold tracking-wide fs-11">
            Email Address <span className="text-red-500">*</span>
          </legend>
          <Email
            name="txtEmail"
            className="field-secure w-100 bg-transparent border-0 py-2 text-gray-800 fw-medium px-2"
            id="txtEmail"
            placeholder="Email Address"
            style={{ outline: 'none' }}
            onBlur={emailValidCheck}
            onKeyPress={(e) => { if (e.key === ' ') e.preventDefault(); }}
            onChange={(e) => { clearErrors('txtEmail'); eventOnchange(e); }}
            validation={register({
              required: 'Please Enter Valid Email Address',
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]{2,}(?<!\.)$/,
                message: 'Please Enter Valid Email Address',
              },
            })}
          />
        </fieldset>
        {validationMsg?.txtEmail && (
          <p className="error_msg">{validationMsg.txtEmail.message}</p>
        )}

        {/* Phone */}
        <fieldset className="input-wrapper border rounded p-0 pb-1 px-3 position-relative w-100 mb-4 transition-colors rounded-10">
          <legend className="px-1 ms-2 fw-semibold tracking-wide fs-11">
            Mobile Number <span className="text-red-500">*</span>
          </legend>
          <Telephone
            name="txtPhone"
            id="txtPhone"
            className="field-secure w-100 bg-transparent border-0 py-2 text-gray-800 fw-medium px-2"
            placeholder="Enter Mobile Number"
            style={{ outline: 'none' }}
            onBlur={phoneValidCheck}
            onChange={(e) => { clearErrors('txtPhone'); eventOnchange(e); }}
            onKeyPress={(e) => { numberValidation(e); }}
            maxlength="11"
            minLength="11"
            validation={register({
              pattern: { value: /^[0-9]*$/i, message: 'Please Enter Valid Phone Number' },
              required: 'Please Enter Phone Number',
            })}
          />
        </fieldset>
        {validationMsg?.txtPhone && (
          <p className="error_msg">{validationMsg.txtPhone.message}</p>
        )}

        <InfoBanner />

        {/* Spacer so sticky bar never overlaps content */}
        <div style={{ height: '5rem' }} />
      </div>
    </div>
  );
});

export default ContactInfo;